import React, { useEffect, useMemo, useState } from 'react';
import { goToStep, setBack } from '../../../features/stepper/stepperSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import {
  clearErrors,
  setCaseInfoError,
  setErrors,
  setOnlyFetchingCaseParties,
  setOnlyFetchingCreditors,
  startNoticeCreation,
  updateFormData,
  setPendingEditAddressId,
} from '../../../features/upload/uploadSlice';
import {
  addressSchema,
  courtCaseSchema,
  documentsSchema,
  newJerseyPreferencesSchema,
  court341Schema,
  debtor341SchemaCorporation,
  debtor341SchemaIndividual,
  nonSpecialPreferencesSchema,
  EDCAPreferencesSchema,
  orderPreferenceSchema,
  courtLegalSchema,
  directMailingTypeSchema,
  reviewAndSubmitSchema,
} from '../../../utils/validations';
import { AnyObject, ObjectSchema, ValidationError } from 'yup';
import AdditionalInformation from './AdditionalInformation';
import { buildNoticeCreationMapper, getJourneyButtons } from '../utilities/utils';
import use341SpecialRules from '../../../hooks/use341SpecialRules';
import { checkAvailableServiceOptions, fetchCaseInfo, formatParties, sendDataForProcessing } from '../../../features/upload/utils';
import StyledButtonContainer from '../styled/wrapper-styles/StyledButtonsContainer';
import { refreshToken } from 'src/features/user/utils';
import { setCurrentUser } from 'src/features/user/userSlice';
import {
  areCaseNumbersUnique,
  checkIsBetweenFourPmAndMidnightPT,
  isObjectEmpty,
} from 'src/utils/utility-functions';
import { useMappedRecipients } from '../hooks/useMappedRecipients';
import { editCourtLocation } from 'src/features/settings/utils';
import { ADDITIONAL_MML_LOGIC_COURTS } from 'src/utils/constant';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import { Button } from 'src/components/new/Button';
import { OrganizationType } from 'src/utils/enums/organization-type.enum';
import { useFormStepNavigation } from '../hooks/useFormNavigation';
import { InvalidAddressesAlert } from 'src/components/modals/InvalidAddressesAlert';
import { DIRECT_MAILING_PERSONALIZED_ENABLED } from 'src/utils/feature-flags';

interface BkNoticeFormButtonsProps {
  shouldNavigateToSuccess: boolean;
}

const BkNoticeFormButtons: React.FC<BkNoticeFormButtonsProps> = ({ shouldNavigateToSuccess }) => {
  const dispatch = useDispatch<AppDispatch>();

  const formData = useSelector((state: RootState) => state.upload.formData);

  const userOrganizationType = useSelector((state: RootState) => state.user.currentUser.organization.type);

  const shouldJumpToSummary = useSelector((state: RootState) => state.stepper.shouldJumpToSummary);

  const shouldStayOnCurrentIndex = useSelector((state: RootState) => state.stepper.shouldStayOnCurrentIndex);

  const caseInfoState = useSelector((state: RootState) => state.upload.caseInfoState);

  const isMasterMailing = useSelector((state: RootState) => state.upload.formData.caseSource.masterMailingMatrix);

  const isECFUnavailable = useSelector((state: RootState) => state.upload.formData.isECFUnavailable);

  const courtState = useSelector((state: RootState) => state.upload.formData.courtState);

  const courtDistrict = useSelector((state: RootState) => state.upload.formData.courtDistrict);

  const hasFetchedCase = useSelector((state: RootState) => state.upload.hasFetchedCase);

  const directMailingType = useSelector((state: RootState) => state.upload.formData.directMailingType);

  const memoizedHasFetchedCase = useMemo(() => hasFetchedCase, [hasFetchedCase]);

  const currentUser = useSelector((state: RootState) => state.user.currentUser);

  const memoizedHasMultipleChoices = useMemo(
    () => formData.multipleCaseNumbersToChoose,
    [formData.multipleCaseNumbersToChoose],
  );

  const requiredToChooseCaseNumber = useSelector((state: RootState) => state.upload.requiredToChooseCaseNumber);

  const noticeType = useSelector((state: RootState) => state.upload.noticeType);

  const nextButtonName = getJourneyButtons(noticeType, formData, shouldJumpToSummary);

  const { isCorporate, getSpecial341Schema, bk341FormType } = use341SpecialRules();

  const mappedRecipients = useMappedRecipients();

  const docSchema = documentsSchema;

  let specialScreen = nonSpecialPreferencesSchema;

  const lastIndex = Object.keys(nextButtonName).length - 1;

  const [error, setError] = useState('');

  if (formData.courtState === 'New Jersey' && noticeType.type === 'notice-bk') {
    specialScreen = newJerseyPreferencesSchema;
  } else if (
    formData.courtState === 'California' &&
    formData.courtDistrict === 'Eastern' &&
    noticeType.type === 'notice-bk'
  ) {
    specialScreen = EDCAPreferencesSchema;
  } else {
    specialScreen = orderPreferenceSchema;
  }

  const {currentIndex, currentStep, stepPaths, goBack, goNext, navigateToIndex } = useFormStepNavigation()

  /**
   * Navigation guard: when on the address step (currentIndex === 2) and there are
   * unresolved invalid addresses, clicking the button opens the invalid-addresses
   * alert popup instead of proceeding. The button remains enabled so users get
   * clear feedback about why they can't move forward.
   */
  const userUploadedAddresses = formData.userUploadedAddresses;
  const blockingInvalidAddresses = useMemo(
    () =>
      currentStep === 'add-addresses' && formData.caseSource.userSupplied
        ? userUploadedAddresses.filter((a) => (a.isValidAddress === false && !a.isUserApproved) || a.isTooManyLines === true)
        : [],
    [currentStep, formData.caseSource.userSupplied, userUploadedAddresses],
  );
  const hasBlockingInvalidAddresses = blockingInvalidAddresses.length > 0;

  const [invalidAlertOpen, setInvalidAlertOpen] = useState(false);

  const debtorDataSchema = isCorporate ? debtor341SchemaCorporation : debtor341SchemaIndividual;

  const directAddressSchema =
    formData.directMailingType?.type === 'direct_mail_non_personalized' ? addressSchema : null;

  const get341Schema = (): ObjectSchema<AnyObject> => {
    if (currentIndex === 1 && noticeType.type === '341' && !shouldStayOnCurrentIndex) {
      return debtorDataSchema;
    } else {
      return getSpecial341Schema(bk341FormType);
    }
  };

  const getValidationSchema = () => {
    const specialSchema = get341Schema();

    const stepSchemas: { [key: string]: (ObjectSchema<AnyObject> | null)[] } = {
      'notice-bk': [courtCaseSchema, docSchema, addressSchema, specialScreen],
      341: [court341Schema, specialSchema, addressSchema, specialScreen],
      legal: [courtLegalSchema, docSchema, addressSchema, specialScreen],
      direct:
        DIRECT_MAILING_PERSONALIZED_ENABLED
          ? // Flag on: original 5-step (non-personalized) or 4-step (personalized)
            directMailingType?.type === 'direct_mail_non_personalized'
              ? [directMailingTypeSchema, docSchema, directAddressSchema, specialScreen]
              : [directMailingTypeSchema, docSchema, specialScreen]
          : // Flag off: 4-step Non-Personalized — no Select Mailing Type at index 0
            [docSchema, directAddressSchema, specialScreen],
    };

    const currentSchemaToValidated = stepSchemas[noticeType.type as string];

    return currentSchemaToValidated;
  };

  const displayDocumentsError = (
    error: ValidationError,
    validationErrors: {
      [key: string]: string | { id: string; errorMsg: string }[];
    },
  ) => {
    const accumulatedErrors: { id: string; errorMsg: string }[] = [];

    error.inner.forEach((error) => {
      const pdfId = error.params?.id as string;
      const msg = error.message;

      if (msg === 'Please upload at least one document') setError(msg);

      accumulatedErrors.push({
        id: pdfId || 'initial-error-0',
        errorMsg: msg,
      });
    });

    const source = 'uploadedPdfs';

    validationErrors[source] = accumulatedErrors;

    dispatch(setErrors(validationErrors));
  };

  const finishNoticeCreation = async () => {
    const data = buildNoticeCreationMapper(
      { ...formData, clientCategory: userOrganizationType },
      noticeType,
      bk341FormType,
      mappedRecipients,
    );
    dispatch(sendDataForProcessing(data)).then((response) => {
    //@ts-ignore
      window.pendo && window.pendo.track("bankrupcy-notice-set-preferences", {
        includeCertificateOfService: formData.includeCertificateOfService
      });
      if (
        formData.courtState &&
        isObjectEmpty(currentUser.courtLocation) &&
        response.meta.requestStatus !== 'rejected'
      ) {
        dispatch(
          editCourtLocation({
            state: formData.courtState,
            district: formData.courtDistrict,
            division: formData.courtDivision,
          }),
        ).then((response) => {
          if (response.meta.requestStatus !== 'rejected') {
            dispatch(
              setCurrentUser({
                user: {
                  ...currentUser,
                  courtLocation: {
                    state: formData.courtState,
                    district: formData.courtDistrict,
                    division: formData.courtDivision,
                  },
                },
                accessToken: currentUser.accessToken,
              }),
            );
            dispatch(refreshToken());
          }
        });
      }
    });
  };

  const determineShouldProceedWithCase = () => {
    const combinedCaseNumbers = [formData.caseNumber.value, ...formData.jointCaseNumbers?.map((item) => item.value)];

    if (!areCaseNumbersUnique(combinedCaseNumbers)) {
      setError('Ensure case numbers are unique');
      return;
    }

    //initial fetch
    if (
      isMasterMailing &&
      checkIsBetweenFourPmAndMidnightPT() &&
      ADDITIONAL_MML_LOGIC_COURTS.includes(`${courtDistrict ? `${courtDistrict} ${courtState}` : courtState}`)
    ) {
      dispatch(
        setCaseInfoError(
          'Per local rules, notices must be sent the same day as the MML is pulled in your district. To proceed, please switch address source to user supplied or wait till tommorow to submit job',
        ),
      );
    } else {
      if (isECFUnavailable) {
        goNext();
        return;
      }

      if (!hasFetchedCase) {
        dispatch(
          fetchCaseInfo({
            caseNumber: formData.caseNumber.value,
            courtState: formData.courtState ?? '',
            courtDistrict: formData.courtDistrict ?? '',
            jointCaseNumbers: formData.jointCaseNumbers,
            includeCaseParties: formData.caseSource.importCaseParties,
            includeCreditors: formData.caseSource.masterMailingMatrix,
          }),
        );
        return;
      }

      //has fetched multiple cases and has chosen one of the multiple options but hasn't initiated a load
      if (
        hasFetchedCase &&
        requiredToChooseCaseNumber
      ) {
        dispatch(
          fetchCaseInfo({
            caseNumber: formData.caseNumber.value,
            courtState: formData.courtState ?? '',
            courtDistrict: formData.courtDistrict ?? '',
            showFetchedCaseFromMultipleChoice: true,
            jointCaseNumbers: formData.jointCaseNumbers,
            includeCaseParties: formData.caseSource.importCaseParties,
            includeCreditors: formData.caseSource.masterMailingMatrix,
          }),
        ).then((response) => {
          if (response.meta.requestStatus === 'fulfilled')  goNext();
        });
        return;
      }

      if (hasFetchedCase) {
        let shouldRefetchCase = false;
        if (formData.caseSource.importCaseParties && formData.caseParties.length === 0) {
          dispatch(setOnlyFetchingCaseParties(true));
          shouldRefetchCase = true;
        }
        if (formData.caseSource.masterMailingMatrix && formData.creditors.length === 0) {
          dispatch(setOnlyFetchingCreditors(true));
          shouldRefetchCase = true;
        }
        if (shouldRefetchCase) {
          dispatch(
            fetchCaseInfo({
              caseNumber: formData.caseNumber.value,
              courtState: formData.courtState ?? '',
              courtDistrict: formData.courtDistrict ?? '',
              jointCaseNumbers: formData.jointCaseNumbers,
              includeCaseParties: formData.caseSource.importCaseParties,
              includeCreditors: formData.caseSource.masterMailingMatrix,
            }),
          ).then((response) => {
            if (response.meta.requestStatus === 'fulfilled') goNext()
          });
        return;
        }
        goNext()
      } else {
        goNext()
      }
    }
  };

  const goForward = async () => {
    // If there are unresolved invalid addresses, show the alert instead of proceeding.
    if (hasBlockingInvalidAddresses) {
      setInvalidAlertOpen(true);
      return;
    }

    const canFetchCase = {
      'notice-bk': true,
      legal: false,
      '341': true,
      direct: false,
    };

    if (currentIndex === lastIndex) {
      if (currentUser.organization.type !== OrganizationType.CHAPTER_13_TRUSTEE) {
        try {
          await reviewAndSubmitSchema.validate(formData, {
            abortEarly: false,
          });
        } catch (err) {
          if (err instanceof ValidationError) {
            const validationErrors: { [key: string]: string } = {};
            err.inner.forEach((error) => {
              validationErrors[error.path || ''] = error.message;
            });
            dispatch(setErrors(validationErrors));
          } else {
            console.error('Unexpected error:', err);
          }
          return;
        }
        dispatch(clearErrors());
      }

      dispatch(startNoticeCreation());
      finishNoticeCreation();
      return;
    }

    if (currentIndex === 0 && noticeType.type === 'notice-bk') {
      //@ts-ignore
      window.pendo && window.pendo.track("bankrupcy-notice-court-location", {
        courtState: formData.courtState,
        courtDistrict: formData.courtDistrict === 'Add...' ? null : formData.courtDistrict
      });
    }

    if (currentIndex === 1 && directMailingType?.type === 'direct_mail_personalized') {
      dispatch(checkAvailableServiceOptions());
    }

    if (currentStep === 'add-addresses') {
      dispatch(checkAvailableServiceOptions());
    }

    try {
      let schemas = getValidationSchema();

      if (schemas[currentIndex]) {
        await schemas[currentIndex]!.validate(formData, {
          abortEarly: false,
        });
      }

      dispatch(clearErrors());

      if (shouldJumpToSummary) {
        // Review & Submit is always the last step regardless of flow type or step count.
        const summaryStep = stepPaths.length - 1;
        dispatch(goToStep(summaryStep));
        navigateToIndex(summaryStep);
        return;
      }

      if (currentIndex === 0 && canFetchCase[noticeType.type]) {
        determineShouldProceedWithCase();
        return;
      }
      if (
        currentIndex === lastIndex - 1 &&
        hasFetchedCase &&
        formData.certificatePreferences.shouldIncludeNEF &&
        formData.caseParties.length === 0
      ) {
        dispatch(setOnlyFetchingCaseParties(true));
        dispatch(
          fetchCaseInfo({
            caseNumber: formData.caseNumber.value,
            courtState: formData.courtState ?? '',
            courtDistrict: formData.courtDistrict ?? '',
            jointCaseNumbers: formData.jointCaseNumbers,
            includeCaseParties: true,
            includeCreditors: formData.caseSource.masterMailingMatrix,
          }),
        ).then((response) => {
          if (response.meta.requestStatus === 'fulfilled') {
            const updatedCaseParties = formatParties(response.payload).map((party: any) => ({
              ...party,
              isCmEcf: true,
              isFirstClass: false,
              isCertified: false,
              isOvernight: false,
            }));
            dispatch(updateFormData({ caseParties: updatedCaseParties }));
            goNext();
          }
        });
        return;
      }
      goNext();
    } catch (err: unknown) {
      if (err instanceof ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        //if we're on step for validating the documents
        if (currentStep === 'add-documents' && noticeType.type !== '341') {
          displayDocumentsError(err, validationErrors);
          return;
        }

        err.inner.forEach((error) => {
          if (error.path === 'caseNumbers') {
            setError('Ensure each case number is unique');
          }
          validationErrors[error.path || ''] = error.message;
        });

        dispatch(setErrors(validationErrors));
      } else {
        console.error('Unexpected error:', err);
      }
    }
  };

  const goBackWard = () => {
    dispatch(setBack());
    dispatch(clearErrors());
    goBack();
  };

  const isInitialStep: boolean = currentIndex === 0;

  useEffect(() => {
    if (
      Object.values(memoizedHasMultipleChoices).length === 0 &&
      memoizedHasFetchedCase &&
      currentIndex !== lastIndex
    ) {
      goNext();
    }
  }, [memoizedHasMultipleChoices, memoizedHasFetchedCase]);

  return (
    <StyledButtonContainer>
      <BkSnackBar
        open={!!error}
        snackMessage={error}
        handleCloseSnackbar={() => {
          setError('');
        }}
      />
      <AdditionalInformation currentIndex={currentIndex} />
      <div style={{ display: 'flex', flexDirection: 'row', gap: '0.9375rem', marginRight: '3.125rem' }}>
        {!isInitialStep && (
          <Button type="outline" sx={{ width: '15.625rem' }} onClick={goBackWard}>
            Go Back
          </Button>
        )}
        <Button
          type="primary"
          sx={{ width: '15.625rem' }}
          onClick={goForward}
          disabled={caseInfoState?.status === 'pending'}
        >
          {nextButtonName[currentIndex]}
        </Button>
      </div>

      {/* Invalid addresses alert — triggered when user tries to proceed with unresolved invalid addresses */}
      <InvalidAddressesAlert
        open={invalidAlertOpen}
        onClose={() => setInvalidAlertOpen(false)}
        invalidAddresses={blockingInvalidAddresses}
        onEditAddress={(addr) => {
          setInvalidAlertOpen(false);
          dispatch(setPendingEditAddressId(addr.id));
        }}
      />
    </StyledButtonContainer>
  );
};

export default BkNoticeFormButtons;
